/**
 * Read-only payer enrollments for the authenticated Practitioner.
 * Data comes from the bigquery-query bot; NPI is never sent from the UI.
 */

import { Alert, Badge, Loader, Stack, Text } from '@mantine/core';
import { useMedplum } from '@medplum/react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import { formatUsDateShort } from '@/lib/dateHelpers';
import {
  ENROLLMENTS_EMPTY_MESSAGE,
  billingThroughBannerText,
  enrollmentRowKey,
  enrollmentStatusBadgeColor,
  fetchProviderEnrollments,
  uniquePayerStates,
  type ProviderEnrollmentRow,
} from '@/lib/providerEnrollmentsService';

const EMPTY_ENROLLMENT_ROWS: ProviderEnrollmentRow[] = [];

function formatApprovalDate(value: string | null): string {
  if (!value) return '—';
  return formatUsDateShort(value) || '—';
}

const COLUMNS: UnifyTableColumn<ProviderEnrollmentRow>[] = [
  {
    key: 'payer_name',
    label: 'Payer',
    render: (row) => <Text size="sm">{row.payer_name ?? '—'}</Text>,
    getValue: (row) => row.payer_name ?? '',
  },
  {
    key: 'payer_state',
    label: 'State',
    render: (row) => (
      <Text size="sm" c={row.payer_state ? undefined : 'dimmed'}>
        {row.payer_state ?? '—'}
      </Text>
    ),
    getValue: (row) => row.payer_state ?? '',
  },
  {
    key: 'enrollment_status',
    label: 'Status',
    render: (row) =>
      row.enrollment_status ? (
        <Badge size="sm" variant="light" color={enrollmentStatusBadgeColor(row.enrollment_status)}>
          {row.enrollment_status}
        </Badge>
      ) : (
        <Text size="sm" c="dimmed">
          —
        </Text>
      ),
    getValue: (row) => row.enrollment_status ?? '',
  },
  {
    key: 'approval_date',
    label: 'Effective date',
    render: (row) => <Text size="sm">{formatApprovalDate(row.approval_date)}</Text>,
    getValue: (row) => formatApprovalDate(row.approval_date),
  },
];

export function ProviderEnrollmentsTab({
  practitionerId,
}: {
  practitionerId?: string;
}): JSX.Element {
  const medplum = useMedplum();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['provider-enrollments', practitionerId ?? 'self'],
    queryFn: () => fetchProviderEnrollments(medplum, practitionerId),
  });

  const rows = data?.rows ?? EMPTY_ENROLLMENT_ROWS;
  const banner = billingThroughBannerText(data?.billingThroughDisplay);
  const stateFilter = useMemo(() => {
    const states = uniquePayerStates(rows);
    return {
      columnKey: 'payer_state',
      placeholder: 'State',
      ariaLabel: 'Filter by state',
      options: states.map((state) => ({ value: state, label: state })),
      w: 140,
    };
  }, [rows]);

  if (isLoading) {
    return (
      <Stack align="center" py="xl" gap="sm">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Loading enrollments…
        </Text>
      </Stack>
    );
  }

  if (isError) {
    return (
      <Alert color="red" title="Unable to load enrollments">
        {error instanceof Error ? error.message : 'Enrollment records could not be loaded.'}
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      {banner && (
        <Text size="sm" c="dimmed">
          {banner}
        </Text>
      )}
      <UnifyTable
        columns={COLUMNS}
        data={rows}
        getRowKey={enrollmentRowKey}
        searchable
        searchPlaceholder="Search enrollments..."
        filter={stateFilter}
        sortable
        pagination
        emptyMessage={ENROLLMENTS_EMPTY_MESSAGE}
      />
    </Stack>
  );
}
